import { format } from 'date-fns';
import type { ReactNode } from 'react';

import { TokenAgeBadge } from '@/components/Badges/TokenAgeBadge';
import { TokenHoldersBadge } from '@/components/Badges/TokenHoldersBadge';
import { TextSkeleton } from '@/components/Skeleton';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useTokenInfo } from '@/hooks/useTokenInfo';

import { fontStyles } from '@/styles/typography';

import { ChartLineIcon } from '@/ui/Icons/ChartLineIcon';
import { CurrencyDollarCircleIcon } from '@/ui/Icons/CurrencyDollarCircleIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { abbreviateNumber, formatTimeElapsed } from '@/utils/numbers';

const Cell = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex shrink-0 flex-col justify-center gap-0.5 whitespace-nowrap border-l border-token-gray-300 px-3 first:border-l-0">
    <span className={fontStyles({ variant: 'LABEL_XS', class: 'text-token-gray-700' })}>
      {label}
    </span>
    <span className={fontStyles({ variant: 'LABEL_MD', class: 'tabular-nums' })}>{children}</span>
  </div>
);

// Display is left to callers, which toggle it per breakpoint.
const cellGroupClassName =
  'h-12 w-fit items-stretch overflow-hidden rounded-sm border border-token-gray-300';

type TokenPanelStatCellsProps = {
  token: TokenResult;
  /** `inline` swaps the labels for icons so the stats fit beside the chips on phones. */
  variant?: 'cells' | 'inline';
  className?: string;
};

/** Market cap, 24h volume, holders and age for the homepage panel header. */
export const TokenPanelStatCells = ({
  token,
  variant = 'cells',
  className,
}: TokenPanelStatCellsProps) => {
  const { data: tokenInfo, isLoading } = useTokenInfo(token.address, token.chainId);
  const { formatFiat } = useFiatDisplay();

  if (variant === 'inline') {
    const { marketCap, volume24h, holders, creationTime } = tokenInfo?.stats ?? {};
    return (
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            'ml-1 flex-wrap items-center gap-2 text-token-gray-700 leading-[20px]',
            className,
          ],
        })}
      >
        {marketCap != null && (
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <button type="button" className="flex flex-row items-center gap-1 cursor-default">
                <CurrencyDollarCircleIcon className="text-token-gray-700" />
                {formatFiat(marketCap, false, false)}
              </button>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>Market cap</MatchaTooltipContent>
            <MatchaTooltipPopover sideOffset={8} />
            <MatchaTooltipBottomSheet header="Market cap" />
          </MatchaTooltip>
        )}
        {volume24h != null && (
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <button
                type="button"
                aria-label="24h Volume"
                className="flex flex-row items-center gap-1 cursor-default"
              >
                <ChartLineIcon aria-hidden className="text-token-gray-700" />
                {formatFiat(volume24h, false, false)}
              </button>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>24h Volume</MatchaTooltipContent>
            <MatchaTooltipPopover sideOffset={8} />
            <MatchaTooltipBottomSheet header="24h Volume" />
          </MatchaTooltip>
        )}
        {holders != null && <TokenHoldersBadge holders={holders} />}
        {creationTime != null && <TokenAgeBadge creationTime={creationTime} />}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={classNames(cellGroupClassName, className)}>
        {['Market cap', '24h Volume', 'Holders', 'Age'].map((label) => (
          <Cell key={label} label={label}>
            <TextSkeleton width={40} height={14} />
          </Cell>
        ))}
      </div>
    );
  }

  const marketCap = tokenInfo?.stats?.marketCap;
  const volume24h = tokenInfo?.stats?.volume24h;
  const holders = tokenInfo?.stats?.holders;
  const creationTime = tokenInfo?.stats?.creationTime;

  if (marketCap == null && volume24h == null && holders == null && creationTime == null)
    return null;

  return (
    <div className={classNames(cellGroupClassName, className)}>
      {marketCap != null && <Cell label="Market cap">{formatFiat(marketCap, false, false)}</Cell>}
      {volume24h != null && <Cell label="24h Volume">{formatFiat(volume24h, false, false)}</Cell>}
      {holders != null && <Cell label="Holders">{abbreviateNumber(holders, 1, true)}</Cell>}
      {creationTime != null && (
        <Cell label="Age">
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <button type="button" className="cursor-default">
                {formatTimeElapsed(creationTime, true)}
              </button>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>
              {format(new Date(creationTime * 1000), 'Pp')}
            </MatchaTooltipContent>
            <MatchaTooltipPopover sideOffset={8} />
            <MatchaTooltipBottomSheet header="Token creation time" />
          </MatchaTooltip>
        </Cell>
      )}
    </div>
  );
};
